import React, { useEffect, useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import AddressAutocomplete from './AddressAutocomplete';
import {
  EMAILJS_SERVICE_ID,
  EMAILJS_TEMPLATE_ID,
  EMAILJS_PUBLIC_KEY,
  EMAILJS_CUSTOMER_TEMPLATE_ID,
  EMAILJS_CUSTOMER_SERVICE_ID,
} from '../emailConfig';
import './BookingForm.css';

function BookingForm() {
  const initialFormState = {
    firstName: '',
    middleInitial: '',
    lastName: '',
    phone: '',
    email: '',
    pickup: '',
    dropoff: '',
    date: '',
    appointmentTime: '',
    time: '',
    serviceType: 'wheelchair',
    returnTrip: false,
    returnDate: '',
    returnTime: '',
    specialAssistance: '',
    message: ''
  };

  const [formData, setFormData] = useState(initialFormState);
  const [step, setStep] = useState('form');          // 'form' or 'review'
  const [confirmed, setConfirmed] = useState(null);   // copy of the sent request, shown in the popup
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const wrapperRef = useRef(null);
  const closeBtnRef = useRef(null);

  // Keep the page from scrolling behind the popup, and focus its Close button
  useEffect(() => {
    if (!confirmed) return undefined;
    document.body.style.overflow = 'hidden';
    if (closeBtnRef.current) closeBtnRef.current.focus({ preventScroll: true });
    return () => { document.body.style.overflow = ''; };
  }, [confirmed]);

  const scrollToTop = () => {
    if (wrapperRef.current) {
      const y = wrapperRef.current.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // Readable labels for the review screen and popup
  const SERVICE_LABELS = { wheelchair: 'Wheelchair Transport', ambulatory: 'Ambulatory / Sitting' };
  const ASSISTANCE_LABELS = {
    '': 'None',
    'door-to-door': 'Door-to-door assistance',
    escort: 'Escort / attendant',
    other: 'Other (see notes)',
  };
  const formatDate = (d) => (d
    ? new Date(d + 'T00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    : '');
  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    return new Date(2000, 0, 1, h, m).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };
  // Order number: AM + date + time (Eastern, to the second) + initials, e.g. AM20261029110542MJ
  const makeOrderNumber = (d) => {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
      }).formatToParts(new Date()).map((p) => [p.type, p.value])
    );
    const initial = (name) => (name || '').trim().replace(/[^A-Za-z]/g, '').charAt(0).toUpperCase() || 'X';
    return `AM${parts.year}${parts.month}${parts.day}${parts.hour}${parts.minute}${parts.second}` +
      `${initial(d.firstName)}${initial(d.lastName)}`;
  };

  const fullName = (d) => `${d.firstName} ${d.middleInitial ? d.middleInitial + '. ' : ''}${d.lastName}`;

  // Everything the rider entered, in the order we show it back
  const summaryRows = (d) => [
    ['Name', fullName(d)],
    ['Phone', d.phone],
    ['Email', d.email || 'Not provided'],
    ['Service', SERVICE_LABELS[d.serviceType]],
    ['Pickup address', d.pickup],
    ['Dropoff address', d.dropoff],
    ['Appointment', `${formatDate(d.date)} at ${formatTime(d.appointmentTime)}`],
    ['Preferred pickup time', d.time ? formatTime(d.time) : "Not specified, we'll schedule it"],
    ['Round trip', d.returnTrip ? `Yes, return ${formatDate(d.returnDate)} at ${formatTime(d.returnTime)}` : 'No'],
    ['Special assistance', ASSISTANCE_LABELS[d.specialAssistance]],
    ['Notes', d.message || 'None'],
  ];

  // Earliest selectable date is today (local time)
  const now = new Date();
  const today = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().split('T')[0];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : (name === 'middleInitial' ? value.substring(0, 1).toUpperCase() : value)
    });
  };

  // Pickup and dropoff boxes send back the address text directly
  const handleAddressChange = (field) => (text) => {
    setFormData((prev) => ({ ...prev, [field]: text }));
  };

  // Step 1: the form is valid, so show the review screen
  const handleSubmit = (e) => {
    e.preventDefault();
    setError(false);
    setStep('review');
    scrollToTop();
  };

  // Step 2: the rider confirmed their details, so send the request
  const sendRequest = () => {
    setLoading(true);
    setError(false);
    const orderNumber = makeOrderNumber(formData);

    // Map your form state into template parameters
    const templateParams = {
      form_type: 'Booking Request',
      order_number: orderNumber,
      passenger_name: `${formData.firstName} ${formData.middleInitial ? formData.middleInitial + '. ' : ''}${formData.lastName}`,
      phone: formData.phone,
      email: formData.email || 'N/A',
      service_type: formData.serviceType,
      pickup_address: formData.pickup,
      dropoff_address: formData.dropoff,
      pickup_date: formData.date,
      appointment_time: formData.appointmentTime,
      pickup_time: formData.time || 'Not specified (we will schedule it)',
      round_trip: formData.returnTrip ? 'Yes' : 'No',
      return_date: formData.returnTrip ? formData.returnDate : 'N/A',
      return_time: formData.returnTrip ? formData.returnTime : 'N/A',
      special_assistance: formData.specialAssistance || 'None',
      additional_info: formData.message || 'None',
      message: formData.message || 'None',
      reply_to: formData.email || ''
    };

    // Copy for the customer, only when they gave an email and the template is set up
    const customerEmailOn =
      formData.email && EMAILJS_CUSTOMER_TEMPLATE_ID && !EMAILJS_CUSTOMER_TEMPLATE_ID.startsWith('PASTE');
    const customerParams = {
      to_email: formData.email,
      email: formData.email,       // same address, in case the template's To Email uses {{email}}
      reply_to: 'yketema@getabsolutemobility.com',
      to_name: formData.firstName,
      order_number: orderNumber,
      appointment_date: formatDate(formData.date),
      appointment_time: formatTime(formData.appointmentTime),
      pickup_time: formData.time ? formatTime(formData.time) : "We'll schedule it and confirm when we call",
      pickup_address: formData.pickup,
      dropoff_address: formData.dropoff,
      service_type: SERVICE_LABELS[formData.serviceType],
      round_trip: formData.returnTrip
        ? `Yes, return ${formatDate(formData.returnDate)} at ${formatTime(formData.returnTime)}`
        : 'No',
      special_assistance: ASSISTANCE_LABELS[formData.specialAssistance],
    };

    emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY)
      .then(async (response) => {
        console.log('Email successfully sent!', response.status, response.text);
        // The booking is already received; a problem with the customer copy does not stop it
        let copySent = false;
        if (customerEmailOn) {
          // EmailJS allows about 1 email per second, so wait before sending the customer copy
          const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
          for (let attempt = 1; attempt <= 2 && !copySent; attempt += 1) {
            await wait(attempt === 1 ? 1200 : 2500);
            try {
              const customerService = EMAILJS_CUSTOMER_SERVICE_ID && !EMAILJS_CUSTOMER_SERVICE_ID.startsWith('PASTE')
                ? EMAILJS_CUSTOMER_SERVICE_ID
                : EMAILJS_SERVICE_ID;
              await emailjs.send(customerService, EMAILJS_CUSTOMER_TEMPLATE_ID, customerParams, EMAILJS_PUBLIC_KEY);
              copySent = true;
            } catch (copyError) {
              console.warn(`Customer confirmation email failed (attempt ${attempt}):`, copyError);
            }
          }
        }
        setConfirmed({ ...formData, orderNumber, copySent }); // opens the popup, which stays until Close is clicked
        setFormData(initialFormState);  // clear the form for next time
        setStep('form');
      })
      .catch((error) => {
        console.error('Failed to send email:', error);
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="booking-wrapper" ref={wrapperRef}>
      <div className="booking-header">
        <h2>Booking Request</h2>
        <p>Fill out the form below and we'll get back to you within 30 minutes to confirm your ride.</p>
      </div>
      
      {step === 'review' && (
        <div className="booking-form review-panel">
          <h3 className="section-title">Please review your ride details</h3>
          <p className="review-intro">Make sure everything is correct, then tap <strong>Confirm &amp; Submit</strong>.</p>
          {error && (
            <div className="error-message" role="alert">
              Something went wrong sending your request. Please try again or call us at{' '}
              <a href="tel:+12028162344">(202) 816-2344</a>.
            </div>
          )}
          <dl className="summary-list">
            {summaryRows(formData).map(([label, value]) => (
              <div className="summary-row" key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          <div className="review-actions">
            <button type="button" className="edit-btn" onClick={() => { setStep('form'); scrollToTop(); }} disabled={loading}>
              ← Edit Details
            </button>
            <button type="button" className="submit-btn" onClick={sendRequest} disabled={loading}>
              {loading ? 'Sending Request...' : 'Confirm & Submit'}
            </button>
          </div>
        </div>
      )}

      {step === 'form' && (
      <form className="booking-form" onSubmit={handleSubmit}>
        {error && (
          <div className="error-message" role="alert">
            Something went wrong sending your request. Please try again or call us at{' '}
            <a href="tel:+12028162344">(202) 816-2344</a>.
          </div>
        )}
        
        {/* Contact Information Section */}
        <div className="form-section">
          <h3 className="section-title">Contact Information</h3>
          
          <div className="form-row name-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input 
                type="text" 
                id="firstName"
                name="firstName" 
                placeholder="John" 
                value={formData.firstName} 
                onChange={handleChange} 
                required 
              />
            </div>
            <div className="form-group mi-input">
              <label htmlFor="middleInitial">M.I.</label>
              <input 
                type="text" 
                id="middleInitial"
                name="middleInitial" 
                placeholder="D" 
                maxLength="1"
                value={formData.middleInitial} 
                onChange={handleChange} 
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input 
                type="text" 
                id="lastName"
                name="lastName" 
                placeholder="Doe" 
                value={formData.lastName} 
                onChange={handleChange} 
                required 
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <input 
                type="tel" 
                id="phone"
                name="phone" 
                placeholder="(555) 123-4567" 
                value={formData.phone} 
                onChange={handleChange} 
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email Address (recommended)</label>
              <input 
                type="email" 
                id="email"
                name="email" 
                placeholder="john@example.com" 
                value={formData.email} 
                onChange={handleChange} 
              />
              <small className="field-hint">Add your email to get a copy of your ride request and order number. No email? No problem, we'll call you.</small>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group full-width-row">
              <label htmlFor="serviceType">Service Type *</label>
              <select name="serviceType" id="serviceType" value={formData.serviceType} onChange={handleChange} required>
                <option value="wheelchair">Wheelchair Transport</option>
                <option value="ambulatory">Ambulatory / Sitting</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* Trip Details Section */}
        <div className="form-section">
          <h3 className="section-title">Trip Details</h3>
          
          <div className="form-row">
            <div className="form-group full-width-row">
              <label htmlFor="pickup">Pickup Address *</label>
              <AddressAutocomplete
                id="pickup"
                name="pickup"
                placeholder="Start typing the address"
                value={formData.pickup}
                onChange={handleAddressChange('pickup')}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width-row">
              <label htmlFor="dropoff">Dropoff Address *</label>
              <AddressAutocomplete
                id="dropoff"
                name="dropoff"
                placeholder="Start typing the address"
                value={formData.dropoff}
                onChange={handleAddressChange('dropoff')}
                required
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="date">Appointment Date *</label>
              <input 
                type="date" 
                id="date"
                name="date" 
                min={today}
                value={formData.date} 
                onChange={handleChange} 
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="appointmentTime">Appointment Time *</label>
              <input 
                type="time" 
                id="appointmentTime"
                name="appointmentTime" 
                value={formData.appointmentTime} 
                onChange={handleChange} 
                required 
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="time">Preferred Pickup Time (optional)</label>
              <input 
                type="time" 
                id="time"
                name="time" 
                value={formData.time} 
                onChange={handleChange} 
              />
              <small className="field-hint">Leave blank and we'll pick a time that gets you there before your appointment.</small>
            </div>
          </div>
          
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input 
                type="checkbox" 
                name="returnTrip" 
                checked={formData.returnTrip} 
                onChange={handleChange} 
              />
              <span>Need round trip / return transport?</span>
            </label>
          </div>
          
          {formData.returnTrip && (
            <div className="form-row return-trip-fields">
              <div className="form-group">
                <label htmlFor="returnDate">Return Date</label>
                <input 
                  type="date" 
                  id="returnDate"
                  name="returnDate" 
                  min={formData.date || today}
                  value={formData.returnDate} 
                  onChange={handleChange} 
                  required={formData.returnTrip}
                />
              </div>
              <div className="form-group">
                <label htmlFor="returnTime">Return Time (approx.)</label>
                <input 
                  type="time" 
                  id="returnTime"
                  name="returnTime" 
                  value={formData.returnTime} 
                  onChange={handleChange} 
                  required={formData.returnTrip}
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Special Requirements Section */}
        <div className="form-section">
          <h3 className="section-title">Special Requirements</h3>
          <div className="form-group">
            <label htmlFor="specialAssistance">Do you need any special assistance?</label>
            <select name="specialAssistance" id="specialAssistance" value={formData.specialAssistance} onChange={handleChange}>
              <option value="">No special assistance needed</option>
              <option value="door-to-door">Door-to-door assistance</option>
              <option value="escort">Need escort / attendant</option>
              {/* <option value="oxygen">Oxygen support</option> */}
              <option value="other">Other (please specify)</option>
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="message">Additional Information or Special Instructions</label>
            <textarea 
              id="message"
              name="message" 
              placeholder="Please let us know about any special needs, medical equipment, or specific requirements..." 
              value={formData.message} 
              onChange={handleChange} 
              rows="4"
            ></textarea>
          </div>
        </div>
        
        <div className="form-actions">
          <button type="submit" className="submit-btn">
            Review My Ride <span className="arrow">→</span>
          </button>
          <p className="form-note">* Required fields. You'll review your details before sending.</p>
        </div>
      </form>
      )}

      {confirmed && (
        <div className="confirm-overlay">
          <div className="confirm-modal" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
            <div className="confirm-check" aria-hidden="true">✓</div>
            <h2 id="confirm-title">Your Ride Request Is In!</h2>
            <div className="confirm-order">
              <span>Order number</span>
              <strong>{confirmed.orderNumber}</strong>
            </div>
            <p className="confirm-lead">
              Thank you, {confirmed.firstName}. We received your request and will call you at{' '}
              <strong>{confirmed.phone}</strong> within 30 minutes to confirm your ride.
            </p>
            {confirmed.copySent && (
              <p className="confirm-emailed">
                ✉️ We also emailed a copy to <strong>{confirmed.email}</strong>.
              </p>
            )}
            <dl className="summary-list confirm-summary">
              <div className="summary-row"><dt>Appointment</dt><dd>{formatDate(confirmed.date)} at {formatTime(confirmed.appointmentTime)}</dd></div>
              <div className="summary-row"><dt>Pickup</dt><dd>{confirmed.pickup}</dd></div>
              <div className="summary-row"><dt>Dropoff</dt><dd>{confirmed.dropoff}</dd></div>
              <div className="summary-row"><dt>Service</dt><dd>{SERVICE_LABELS[confirmed.serviceType]}{confirmed.returnTrip ? ', round trip' : ''}</dd></div>
            </dl>
            <p className="confirm-help">
              Need to change something? Call us at <a href="tel:+12028162344">(202) 816-2344</a> and
              have your order number ready.
            </p>
            <button type="button" ref={closeBtnRef} className="confirm-close" onClick={() => setConfirmed(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default BookingForm;